import { z } from "zod";
import { APPOINTMENT_STATUSES } from "./types";

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Fecha inválida");
const id = z.string().min(1).max(128).regex(/^[\w-]+$/);

export const availabilityInput = z.object({
  serviceId: id,
  practitionerId: id.optional(),
  from: isoDate,
  days: z.number().int().min(1).max(14).default(7),
});
export type AvailabilityInput = z.input<typeof availabilityInput>;

export const holdInput = z.object({
  serviceId: id,
  practitionerId: id,
  chairId: id,
  start: z.iso.datetime(),
  idempotencyKey: z.string().min(16).max(64),
});
export type HoldInput = z.input<typeof holdInput>;

export const patientContact = z.object({
  firstName: z.string().trim().min(1, "Ingresá tu nombre").max(80),
  lastName: z.string().trim().min(1, "Ingresá tu apellido").max(80),
  email: z.email("Revisá el email").max(160),
  phone: z
    .string()
    .trim()
    .regex(/^\+?[\d\s()-]{8,20}$/, "Ingresá un teléfono con código de área"),
  documentId: z
    .string()
    .trim()
    .regex(/^\d{7,9}$/, "Ingresá tu DNI sin puntos"),
});
export type PatientContact = z.infer<typeof patientContact>;

/** Profile completion after Google sign-in: everything except the email, which comes from Google. */
export const patientProfileInput = patientContact.omit({ email: true });
export type PatientProfileInput = z.infer<typeof patientProfileInput>;

export const bookingInput = z.object({
  holdId: id,
  contact: patientContact,
  notes: z.string().trim().max(500).optional(),
  consentPrivacy: z.literal(true, { error: "Necesitamos tu consentimiento para continuar" }),
  consentReminders: z.boolean().default(true),
  idempotencyKey: z.string().min(16).max(64),
});
export type BookingInput = z.input<typeof bookingInput>;

export const manageTokenInput = z.object({ token: z.string().min(32).max(128) });

export const cancelInput = z.object({
  appointmentId: id.optional(),
  token: z.string().min(32).max(128).optional(),
  reason: z.string().trim().max(300).optional(),
});

export const rescheduleInput = z.object({
  appointmentId: id.optional(),
  token: z.string().min(32).max(128).optional(),
  holdId: id,
  idempotencyKey: z.string().min(16).max(64),
});

export const statusInput = z.object({
  appointmentId: id,
  status: z.enum(APPOINTMENT_STATUSES),
  reason: z.string().trim().max(300).optional(),
});

export const staffBookingInput = z.object({
  holdId: id,
  patientId: id.optional(),
  contact: patientContact.partial({ email: true, documentId: true }).optional(),
  notes: z.string().trim().max(500).optional(),
  status: z.enum(["pending_confirmation", "confirmed"]).default("confirmed"),
  idempotencyKey: z.string().min(16).max(64),
});

export const assistantInput = z.object({
  text: z.string().trim().min(3, "Contanos un poco más").max(800),
  source: z.enum(["web", "whatsapp", "email", "admin"]).default("web"),
  contact: z
    .object({ name: z.string().max(120).optional(), phone: z.string().max(40).optional(), email: z.string().max(160).optional() })
    .optional(),
});
export type AssistantInput = z.input<typeof assistantInput>;

export const attachContactInput = z.object({
  requestId: id,
  name: z.string().trim().min(2, "Ingresá tu nombre").max(120),
  phone: z
    .string()
    .trim()
    .regex(/^\+?[\d\s()-]{8,20}$/, "Ingresá un teléfono con código de área"),
});

export const emailInput = z.object({ email: z.email() });
export const siteStatusInput = z.object({ maintenance: z.boolean() });

export const setRoleInput = z.object({
  email: z.email(),
  role: z.enum(["owner", "branch_admin", "receptionist", "dentist", "specialist", "assistant", "billing", "auditor"]),
  practitionerId: id.optional(),
});
